import React, { useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import { Table } from '../../../../components/Table'; 
import modalStyles from '../../../../css/components/overlaymodal-animations.module.css'; 
import styles from '../css/patient-follow-up-modal.module.css';
import DeleteButton from '../../../../components/deleteButton';
import { fetchFollowUp, saveFollowUp } from '../../../../api/dispensary/script/processScriptApi.js';

const PatientFollowUpModal = ({ onClose, scriptNumber, onSaved }) => {
  const [formData, setFormData] = useState([
    { id: Date.now(), question: '', answer: '', notes: '' },
  ]);
  const [loading, setLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const handleQuestionChange = (row, value) => {
    const updatedData = formData.map((item) =>
      item.id === row.id ? { ...item, question: value } : item
    );
    setFormData(updatedData);
  };

  const handleAnswerChange = (row, value) => {
    const updatedData = formData.map(item => 
      item.id === row.id ? { ...item, answer: value } : item
    );
    setFormData(updatedData);
  };

  const handleNotesChange = (row, value) => {
    const updatedData = formData.map(item => 
      item.id === row.id ? { ...item, notes: value } : item
    );
    setFormData(updatedData);
  };

  const handleAddRow = () => {
    setFormData((prev) => [
      ...prev,
      { id: Date.now() + Math.floor(Math.random() * 1000), question: '', answer: '', notes: '' },
    ]);
  };

  const handleDeleteRow = (row) => {
    setFormData((prev) => prev.filter((item) => item.id !== row.id));
  };

  const handleSave = async () => {
    const questions = formData.map(r => (r.question || '').toString());
    const answers = formData.map(r => (r.answer || '').toString());
    const notes = formData.map(r => (r.notes || '').toString());
    setLoading(true);
    try {
      await saveFollowUp(scriptNumber, { questions, answers, notes });
      if (typeof onSaved === 'function') onSaved({ questions, answers, notes });
      onClose();
    } catch (e) {
      console.error('Failed to save follow up', e);
      alert(e?.message || 'Failed to save follow up');
    } finally {
      setLoading(false);
    }
  };

  const wrapStyle = { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%' };
  const columns = [
    { 
      header: 'Question', 
      accessor: 'question',
      render: (value, row) => readOnly ? (
        <span style={wrapStyle}>{row.question || ''}</span>
      ) : (
        <input
          type="text"
          value={row.question}
          onChange={(e) => handleQuestionChange(row, e.target.value)}
          className={styles.questionInput}
          style={{ width: '100%' }}
          placeholder="Enter question"
        />
      ),
    },
    {
      header: 'Yes / No',
      accessor: 'answer',
      render: (value, row) => readOnly ? (
        <span style={wrapStyle}>{row.answer ? (row.answer.toLowerCase() === 'yes' ? 'Yes' : row.answer.toLowerCase() === 'no' ? 'No' : row.answer) : ''}</span>
      ) : (
        <div className={styles.yesNoContainer}>
          <label className={styles.checkboxLabel}>
            <input
              type="radio"
              name={`answer-${row.id}`}
              checked={row.answer === 'yes'}
              onChange={() => handleAnswerChange(row, 'yes')}
              className={styles.checkbox}
            />
            Yes
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="radio"
              name={`answer-${row.id}`}
              checked={row.answer === 'no'}
              onChange={() => handleAnswerChange(row, 'no')}
              className={styles.checkbox}
            />
            No
          </label>
        </div>
      ),
    },
    {
      header: 'Notes',
      accessor: 'notes',
      render: (value, row) => readOnly ? (
        <span style={wrapStyle}>{row.notes || ''}</span>
      ) : (
        <input
          type="text"
          value={row.notes}
          onChange={(e) => handleNotesChange(row, e.target.value)}
          className={styles.notesInput}
          style={{ width: '100%' }}
          placeholder="Enter notes"
        />
      ),
    },
  ];
  if (!readOnly) {
    columns.push({
      header: 'Actions',
      accessor: 'actions',
      render: (value, row) => (
        <DeleteButton onClick={() => handleDeleteRow(row)} />
      ),
    });
  }

  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  // Load existing follow-up and switch to read-only if present
  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!scriptNumber) return;
      try {
        const data = await fetchFollowUp(scriptNumber);
        if (ignore) return;
        if (data && Array.isArray(data.questions)) {
          const rows = data.questions.map((q, idx) => ({
            id: `${idx}-${Date.now()}`,
            question: q || '',
            answer: (data.answers && data.answers[idx]) || '',
            notes: (data.notes && data.notes[idx]) || '',
          }));
          setFormData(rows.length ? rows : [{ id: Date.now(), question: '', answer: '', notes: '' }]);
          setReadOnly(true);
        }
      } catch (e) {
        // If fetch fails, stay in editable mode
      }
    }
    load();
    return () => { ignore = true; };
  }, [scriptNumber]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 300); 
  };

  return (
    <div
      className={`${modalStyles.modalOverlay} 
        ${isVisible ? modalStyles.show : ""} 
        ${isClosing ? modalStyles.hide : ""}`}
    >
      <div className={`${modalStyles.modalContent} ${styles.modalContent}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalHeading}>Patient Follow Up : {scriptNumber}</h2>
          <button className={modalStyles.closeButton} onClick={handleClose}>
            &times;
          </button>
        </div>

        {!readOnly && (
          <div className={styles.sectionRow}>
            <h3></h3>
            <button className={styles.addButton} onClick={handleAddRow}>+ Add</button>
          </div>
        )}

        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
          <Table columns={columns} data={formData} />
        </div>

        <div className={styles.buttonContainer}>
          {readOnly ? (
            <Button onClick={handleClose} variant="primary">Close</Button>
          ) : (
            <Button onClick={handleSave} variant="primary">
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientFollowUpModal;
